import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { Save, ArrowLeft, Package, MapPin, Eye, Box, Upload, X, Paperclip, Calendar, FileCheck, FileText } from "lucide-react";
import { createPageUrl, calculateBundles } from "@/utils";
import PDFViewerModal from "@/components/PDFViewerModal";

/** The field is in ounces; piece_weight_lbs is in pounds. */
const OUNCES_PER_POUND = 16;
import PostageStatementAddressModal from "@/components/PostageStatementAddressModal";
import ShippingOverrideSection from "@/components/ShippingOverrideSection";
import UploadDocumentsSection from "@/components/review/UploadDocumentsSection";
import EditPostOfficeModal from "@/components/review/EditPostOfficeModal";
import EditRouteModal from "@/components/review/EditRouteModal";

export default function ReviewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [parsedData, setParsedData] = useState(null);
  const [mailingDate, setMailingDate] = useState("");
  const [mailingName, setMailingName] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  /**
   * Ounces, as printers weigh a mail piece and as the field is labelled.
   * Stored as pounds, because that is what the PVDS prints and what
   * piece_weight_lbs means - so it is converted on the way in and out. Sending
   * the ounces figure straight through overstated every weight sixteenfold.
   */
  const [pieceWeight, setPieceWeight] = useState("");
  const [totalWeight, setTotalWeight] = useState("0");
  const [permitInfo, setPermitInfo] = useState({
    permit_holder: "",
    permit_number: "",
    permit_type: "Permit Imprint",
    post_office: "",
    post_office_address: "",
  });
  /**
   * USPS Delivery Type, as printed on the source 3602 and reprinted on every
   * facing slip. "Business & Residential" covers every delivery point on the
   * route; "Residential Only" skips businesses. It is not the carrier route
   * class - a rural route can be either.
   */
  const [deliveryType, setDeliveryType] = useState("Business & Residential");
  const [routes, setRoutes] = useState([]);
  const [editingRoute, setEditingRoute] = useState(null);
  const [editingRouteSearch, setEditingRouteSearch] = useState("");
  const [searchingRouteDestination, setSearchingRouteDestination] = useState(false);
  const [routeSearchLLMResult, setRouteSearchLLMResult] = useState(null);
  const [editingPostOffice, setEditingPostOffice] = useState(null);
  const [searchingPostOffice, setSearchingPostOffice] = useState(false);
  const [postOfficeSearchName, setPostOfficeSearchName] = useState("");
  const [boxSizes, setBoxSizes] = useState([]);
  const [paperStocks, setPaperStocks] = useState([]);
  const [selectedPaperStockId, setSelectedPaperStockId] = useState("");
  const [selectedContainerType, setSelectedContainerType] = useState("");
  const [packingResult, setPackingResult] = useState(null);
  const [packing, setPacking] = useState(false);
  const [packingError, setPackingError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [existingMailingId, setExistingMailingId] = useState(null);

  /**
   * Show a confirmation, replacing whatever was there.
   *
   * Each message used to schedule its own clear, so a timer started by an
   * earlier one would blank a later one part-way through - "Mailing updated"
   * disappearing a moment after a document upload had said something else.
   */
  const successTimer = useRef(null);
  const announce = (message) => {
    if (successTimer.current) clearTimeout(successTimer.current);
    setSuccess(message);
    successTimer.current = setTimeout(() => {
      setSuccess("");
      successTimer.current = null;
    }, 4000);
  };

  // New states for document upload and preview
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [previewPDF, setPreviewPDF] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // New states for acceptance PDF
  const [acceptancePdfTemplate, setAcceptancePdfTemplate] = useState(null);
  const [uploadingAcceptance, setUploadingAcceptance] = useState(false);
  const [isDraggingAcceptance, setIsDraggingAcceptance] = useState(false);
  const acceptanceFileInputRef = useRef(null);

  // New states for Postage Statement PDF
  const [postageStatementTemplate, setPostageStatementTemplate] = useState(null);
  const [uploadingPostageStatement, setUploadingPostageStatement] = useState(false);
  const [isDraggingPostageStatement, setIsDraggingPostageStatement] = useState(false);
  const postageStatementFileInputRef = useRef(null);

  // NEW: Postage Statement Address states
  const [postageStatementAddress, setPostageStatementAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);

  // Shipping Override state
  const [shippingOverride, setShippingOverride] = useState({ enabled: false });

  // Extraction provenance: how the routes got here and whether they check out.
  const [reconciliation, setReconciliation] = useState(null);
  /** 'text_layer' or 'model' - how the source 3602 was read. */
  const [extractionMethod, setExtractionMethod] = useState(null);
  const [zipResolutions, setZipResolutions] = useState([]);
  const [sourceDocument, setSourceDocument] = useState(null);

  useEffect(() => {
    const proposal = location.state?.proposal;

    if (!proposal && !location.state?.parsedData && !location.state?.existingMailing) {
      navigate(createPageUrl('Home'));
      return;
    }

    // A freshly extracted 3602 arrives as a proposal from the server. Translate
    // it into the shape this screen already works in, and hold onto the
    // reconciliation — the save button stays disabled while it is unbalanced,
    // because those piece counts end up on a USPS postage statement.
    if (proposal) {
      setReconciliation(proposal.reconciliation ?? null);
      setExtractionMethod(proposal.method ?? null);
      setZipResolutions(proposal.zip_resolutions ?? []);
      setSourceDocument(proposal.source_document ?? null);
    }

    const fromProposal = proposal
      ? {
          routes: (proposal.routes ?? []).map((route) => {
            const resolution = (proposal.zip_resolutions ?? []).find((r) => r.zip === route.zip);
            // An assigned ZIP arrives as `post_office`; everything else - most
            // importantly the delivery unit USPS names for a ZIP nobody has
            // confirmed yet - arrives as the first candidate. Reading only the
            // first meant a brand new mailing prefilled nothing at all, even
            // though USPS had already answered.
            const office = resolution?.post_office ?? resolution?.candidates?.[0] ?? null;
            return {
              ...route,
              post_office: office?.name ?? '',
              post_office_id: office?.id ?? null,
              post_office_address: office
                ? {
                    name: office.name,
                    address_line1: office.address_line1,
                    city: office.city,
                    state: office.state,
                    zip: office.zip,
                  }
                : null,
              // The old flag meant "the text didn't look like an address". This
              // one means "no human has confirmed this destination", which is
              // the question that actually matters.
              needs_manual_review: resolution ? resolution.needs_confirmation : true,
            };
          }),
          permit_info: proposal.permit ?? {},
          mailing_date: proposal.mailing_date ?? null,
          delivery_type: proposal.delivery_type || null,
        }
      : null;

    const data = fromProposal || location.state.parsedData || location.state.existingMailing;
    const existingMailing = location.state.existingMailing;

    setParsedData(data);
    setMailingDate(location.state.mailingDate || data.mailing_date || new Date().toISOString().split('T')[0]);
    setMailingName(location.state.mailingName || data.mailing_name || "");
    setOrderNumber(location.state.orderNumber || data.order_number || "");
    setPieceWeight(
      location.state.pieceWeight?.toString() ||
        (data.piece_weight_lbs ? (Number(data.piece_weight_lbs) * OUNCES_PER_POUND).toString() : "")
    );
    // Derived from the piece weight and the piece count rather than stored:
    // total_weight was read in three places, written in none, and was never a
    // column on either the Base44 record or this one.
    setTotalWeight("0");

    let initialSelectedContainerType = ""; 
    let initialSelectedPaperStockId = "";
    let initialPackingResult = null;
    let shouldCalculatePackingOnLoad = false;
    let processedRoutes = [];

    // If editing existing mailing, restore ALL data including box packing
    if (existingMailing) {
      setExistingMailingId(existingMailing.id);
      
      // Load box packing FIRST
      if (existingMailing.box_packing) {
        initialPackingResult = existingMailing.box_packing;
        setPackingResult(initialPackingResult);
      }
      if (existingMailing.selected_container_type) {
        initialSelectedContainerType = existingMailing.selected_container_type;
        setSelectedContainerType(initialSelectedContainerType);
      }
      if (existingMailing.selected_paper_stock_id) {
        initialSelectedPaperStockId = existingMailing.selected_paper_stock_id;
        setSelectedPaperStockId(initialSelectedPaperStockId);
      }
      if (existingMailing.permit_info) {
        setPermitInfo(existingMailing.permit_info);
      }
      if (existingMailing.delivery_type) {
        setDeliveryType(existingMailing.delivery_type);
      }
      // Load additional documents for existing mailing
      if (existingMailing.additional_documents) {
        setAdditionalDocuments(existingMailing.additional_documents);
      }
      // Load acceptance PDF template
      if (existingMailing.acceptance_pdf_template) {
        setAcceptancePdfTemplate(existingMailing.acceptance_pdf_template);
      }
      // Load postage statement PDF template
      if (existingMailing.postage_statement_template) {
        setPostageStatementTemplate(existingMailing.postage_statement_template);
      }
      // Load postage statement address
      if (existingMailing.postage_statement_address) {
        setPostageStatementAddress(existingMailing.postage_statement_address);
      }
      // Load shipping override
      if (existingMailing.shipping_override) {
        setShippingOverride(existingMailing.shipping_override);
      }

      // If a container type was selected but no packing data was saved, we need to calculate
      if (initialSelectedContainerType && initialSelectedPaperStockId && !initialPackingResult) {
        shouldCalculatePackingOnLoad = true;
      }
    } else { // New mailing
      // If selections were pre-set in location state, we should calculate
      if (location.state.selectedContainerType && location.state.selectedPaperStockId) {
        initialSelectedContainerType = location.state.selectedContainerType;
        initialSelectedPaperStockId = location.state.selectedPaperStockId;
        setSelectedContainerType(initialSelectedContainerType);
        setSelectedPaperStockId(initialSelectedPaperStockId);
        shouldCalculatePackingOnLoad = true;
      }
    }

    // Set permit info from parsed data if existing mailing wasn't set (or override if parsed data exists)
    if (data.delivery_type) {
      setDeliveryType(data.delivery_type);
    }
    if (data.permit_info && !existingMailing) {
      setPermitInfo(prev => ({ ...prev, ...data.permit_info }));
    }

    // Process routes and calculate bundles with proper rules
    if (data.routes) {
      processedRoutes = data.routes.map(route => {
        const pieces = parseInt(route.pieces) || 0;
        const bundleCalc = calculateBundles(pieces);
        
        return {
          ...route,
          pieces,
          bundles: bundleCalc.bundles,
          lastBundlePieces: bundleCalc.lastBundlePieces,
          zip: route.zip || "",
          route_type: route.route_type || "",
          post_office: route.post_office || "",
          post_office_address: route.post_office_address || null,
          type: route.type || "Business & Residential",
          needs_manual_review: !route.post_office_address
        };
      });
      setRoutes(processedRoutes);
    }

    // Load company settings
    loadCompanySettings();
    
    // Asynchronously load box sizes, paper stocks and then potentially trigger initial packing calculation
    const loadAndProcessData = async () => {
      try {
        const [boxData, paperData] = await Promise.all([
          api.entities.BoxSize.list(),
          api.entities.PaperStock.list()
        ]);
        // Filter to only active boxes
        const activeBoxes = boxData.filter(box => box.active !== false);
        setBoxSizes(activeBoxes);
        const sortedPapers = [...paperData].sort((a, b) => a.paper_name.localeCompare(b.paper_name, undefined, { numeric: true, sensitivity: 'base' }));
        setPaperStocks(sortedPapers);

        // Packing runs on the server against the saved routes, so it can only
        // happen once the mailing exists. For a mailing being created, the
        // container choice is recorded now and packed immediately after save.
        if (shouldCalculatePackingOnLoad && existingMailing?.id
            && initialSelectedContainerType && initialSelectedPaperStockId) {
          await runPacking(existingMailing.id, initialSelectedContainerType, initialSelectedPaperStockId);
        }
      } catch (err) {
        console.error("Failed to load box sizes or paper stocks", err);
      }
    };
    loadAndProcessData();

  }, [location, navigate]);

  const loadCompanySettings = async () => {
    try {
      const settings = await api.settings.get();
      setPermitInfo(prev => ({
        ...prev,
        permit_holder: prev.permit_holder || settings.company_name || "",
        permit_number: prev.permit_number || settings.permit_number || "",
        permit_type: prev.permit_type || settings.permit_type || "Permit Imprint",
      }));
    } catch (err) {
      console.error("Failed to load settings", err);
    }
  };

  /**
   * Pack the mailing into containers.
   *
   * Runs on the server now. It used to run synchronously in the browser, where
   * an impossible combination — a paper stock larger than every configured box —
   * put it in an unbounded loop that froze the tab. The server reports that as a
   * plain refusal, which is what lands in `error` below.
   */
  const runPacking = async (mailingId, containerType, paperStockId) => {
    setPackingError("");
    setPacking(true);
    try {
      // Before the mailing exists there is nothing to pack against, so the
      // routes go in the request instead. Same calculation either way; the
      // saved form also stores the result on the mailing.
      const { packing } = mailingId
        ? await api.mailings.pack(mailingId, {
            container_type: containerType,
            paper_stock_id: paperStockId,
          })
        : await api.mailings.packPreview({
            container_type: containerType,
            paper_stock_id: paperStockId,
            routes: routes.map((route) => ({
              route: route.route,
              zip: route.zip,
              pieces: parseInt(route.pieces, 10),
              bundles: parseInt(route.bundles, 10) || 0,
              post_office_id: route.post_office_id || null,
              post_office_name: route.post_office || '',
            })),
          });
      setPackingResult(packing);
      return packing;
    } catch (err) {
      setPackingResult(null);
      setPackingError(err.message || "Could not pack this mailing.");
      return null;
    } finally {
      setPacking(false);
    }
  };

  const handlePaperStockChange = async (newPaperStockId) => {
    setSelectedPaperStockId(newPaperStockId);
    setPackingResult(null);
    setPackingError("");

    // Repack straight away when a container is already chosen. Clearing the
    // container instead made changing stock feel like it had undone something.
    if (newPaperStockId && selectedContainerType) {
      await runPacking(existingMailingId, selectedContainerType, newPaperStockId);
    }
  };

  const handleContainerTypeChange = async (newType) => {
    setSelectedContainerType(newType);

    if (!newType || !selectedPaperStockId) {
      setPackingResult(null);
      return;
    }

    await runPacking(existingMailingId, newType, selectedPaperStockId);
  };

  // Memoize expensive calculations that run on every render
  const { totalPieces, totalBundles, destinationPostOffices } = useMemo(() => {
    const pieces = routes.reduce((sum, r) => sum + (parseInt(r.pieces) || 0), 0);
    const bundles = routes.reduce((sum, r) => sum + (parseInt(r.bundles) || 0), 0);
    
    const offices = {};
    routes.forEach(route => {
      const poName = route.post_office || "Default Post Office";
      if (!offices[poName]) {
        offices[poName] = {
          name: poName,
          routes: [],
          route_types: new Set(),
          zip_codes: new Set(),
          address: route.post_office_address
        };
      }
      offices[poName].routes.push(route.route);
      if (route.route_type) {
        offices[poName].route_types.add(route.route_type);
      }
      offices[poName].zip_codes.add(route.zip);
    });

    return { totalPieces: pieces, totalBundles: bundles, destinationPostOffices: offices };
  }, [routes]);

  const handleUpdateRouteDestination = (routeIndex, newPostOffice, newAddress) => {
    setPackingResult(null);
    const updatedRoutes = [...routes];
    updatedRoutes[routeIndex] = {
      ...updatedRoutes[routeIndex],
      post_office: newPostOffice,
      post_office_address: newAddress,
      needs_manual_review: false
    };
    setRoutes(updatedRoutes);
    setEditingRoute(null);
  };

  const handleSearchPostOffice = async () => {
    if (!postOfficeSearchName.trim()) {
      setError("Please enter a post office name to search.");
      return;
    }

    setSearchingPostOffice(true);
    setError("");
    setSuccess("");

    try {
      // Address lookup takes a search string and returns candidates. This used
      // to post a Base44 LLM payload - {prompt, add_context_from_internet,
      // response_json_schema} - which arrived as ?q=[object Object], and then
      // read .name off a response shaped {candidates: [...]}. It failed the
      // truthiness check every time, so the search silently did nothing.
      const { candidates } = await api.extraction.searchAddresses(postOfficeSearchName);
      const result = candidates?.[0];

      if (result?.name && result.address_line1 && result.city && result.state && result.zip) {
        setEditingPostOffice({
          ...editingPostOffice,
          newName: result.name,
          newAddress: {
            address_line1: result.address_line1,
            city: result.city,
            state: result.state,
            zip: result.zip
          }
        });
        announce("Post office found!");
      } else {
        setError("Could not find post office. Please enter details manually or refine your search.");
      }
    } catch (err) {
      console.error("Failed to search for post office:", err);
      setError("Failed to search for post office. Please enter details manually.");
    } finally {
      setSearchingPostOffice(false);
    }
  };

  const handleUpdatePostOffice = () => {
    if (!editingPostOffice.newName || !editingPostOffice.newAddress?.address_line1 || !editingPostOffice.newAddress?.city || !editingPostOffice.newAddress?.state || !editingPostOffice.newAddress?.zip) {
      setError("Please complete all post office address fields before updating.");
      return;
    }

    const updatedRoutes = routes.map(route => {
      if (route.post_office === editingPostOffice.originalName) {
        return {
          ...route,
          post_office: editingPostOffice.newName,
          post_office_address: editingPostOffice.newAddress,
          needs_manual_review: false
        };
      }
      return route;
    });

    setRoutes(updatedRoutes);
    
    // Update box packing if it exists
    if (packingResult) {
      // Same rename against the packed containers. A container names one
      // destination - the packer never mixes offices - so this is a string
      // rather than the array the Base44 shape carried.
      const updatedBoxes = packingResult.boxes.map(box => ({
        ...box,
        post_office_name:
          box.post_office_name === editingPostOffice.originalName
            ? editingPostOffice.newName
            : box.post_office_name
      }));
      
      setPackingResult({
        ...packingResult,
        boxes: updatedBoxes
      });
    }
    
    setEditingPostOffice(null);
    setPostOfficeSearchName("");
    announce("Post office updated for all routes and boxes!");
  };

  const handleSearchRouteDestination = async () => {
    if (!editingRouteSearch.trim()) {
      setError("Please enter a post office name, city, or state to search.");
      return;
    }

    setSearchingRouteDestination(true);
    setError("");
    setSuccess("");

    try {
      const { candidates } = await api.extraction.searchAddresses(editingRouteSearch);
      const result = candidates?.[0];

      if (result?.name && result.address_line1 && result.city && result.state && result.zip) {
        setRouteSearchLLMResult(result);

        announce("Post office found! Click Save to update.");
      } else {
        setError("Could not find post office. Please enter details manually or refine your search.");
      }
    } catch (err) {
      console.error("Failed to search for post office:", err);
      setError("Failed to search for post office. Please enter details manually.");
    } finally {
      setSearchingRouteDestination(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      // Validate all files are PDFs
      const invalidFiles = Array.from(droppedFiles).filter(
        file => !file || !file.name || typeof file.name !== 'string' || !file.name.toLowerCase().endsWith('.pdf')
      );
      
      if (invalidFiles.length > 0) {
        setError("Please drop only PDF files");
        setTimeout(() => setError(""), 3000); // Clear error after 3 seconds
        return;
      }
      
      handleUploadDocuments(droppedFiles);
    }
  };

  // New drag & drop handlers for Acceptance PDF
  const handleAcceptanceDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAcceptance(true);
  };

  const handleAcceptanceDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAcceptance(false);
  };

  const handleAcceptanceDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAcceptance(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name && typeof droppedFile.name === 'string') {
      if (!droppedFile.name.toLowerCase().endsWith('.pdf')) {
        setError("Please drop a PDF file");
        setTimeout(() => setError(""), 3000);
        return;
      }
      handleUploadAcceptancePDF(droppedFile);
    }
  };

  // New drag & drop handlers for Postage Statement PDF
  const handlePostageStatementDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPostageStatement(true);
  };

  const handlePostageStatementDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPostageStatement(false);
  };

  const handlePostageStatementDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPostageStatement(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name && typeof droppedFile.name === 'string') {
      if (!droppedFile.name.toLowerCase().endsWith('.pdf')) {
        setError("Please drop a PDF file");
        setTimeout(() => setError(""), 3000);
        return;
      }
      handleUploadPostageStatementPDF(droppedFile);
    }
  };

  const handleUploadDocuments = async (files) => {
    if (!files || files.length === 0) return;

    const mailingId = await ensureSaved();
    if (!mailingId) return;

    if (!files || files.length === 0) return;

    setUploadingDocs(true);
    setError("");

    try {
      const uploadPromises = Array.from(files)
        .filter(file => file && file.name && typeof file.name === 'string' && file.name.toLowerCase().endsWith('.pdf'))
        .map(async (file) => {
          const { path } = await api.upload('document', file, { mailingId });
          // Determine type based on filename for convenience
          const fileNameLower = file.name.toLowerCase();
          let docType = 'Other';
          if (fileNameLower.includes('postage') || fileNameLower.includes('psform3602')) {
            docType = 'Postage Statement';
          } else if (fileNameLower.includes('scan')) {
            docType = 'Scan Form';
          }
          return {
            name: file.name,
            storage_path: path,
            type: docType,
            upload_date: new Date().toISOString()
          };
      });

      const uploadedDocs = await Promise.all(uploadPromises);
      setAdditionalDocuments(prevDocs => [...prevDocs, ...uploadedDocs]);
      announce(`Uploaded ${uploadedDocs.length} document${uploadedDocs.length !== 1 ? 's' : ''}!`);
    } catch (err) {
      console.error(err);
      setError("Failed to upload documents");
      setTimeout(() => setError(""), 3000);
    } finally {
      setUploadingDocs(false);
    }
  };

  const handleDeleteDocument = (docIndex) => {
    setAdditionalDocuments(additionalDocuments.filter((_, idx) => idx !== docIndex));
  };

  /**
   * The mailing's id, saving it first if it does not have one.
   *
   * Returns null when it could not be saved, having already reported why -
   * callers should simply stop.
   */
  const ensureSaved = async () => {
    if (existingMailingId) return existingMailingId;

    const saved = await persistMailing({ silent: true });
    if (!saved) return null;

    announce('Mailing saved.');
    return saved.id;
  };

  const handleUploadAcceptancePDF = async (file) => {
    if (!file) return;

    // Attaching a document to a mailing that is not saved yet is a perfectly
    // reasonable thing to do, so save it rather than refusing. The upload is
    // stored against the mailing, which is a fact about the storage layout and
    // not something anyone should have to know.
    const mailingId = await ensureSaved();
    if (!mailingId) return;


    setUploadingAcceptance(true);
    setError("");

    try {
      const { path } = await api.upload('mailing-template', file, { mailingId });
      setAcceptancePdfTemplate(path);
      
      // If editing existing mailing, save immediately and clear generated overlay
      await api.entities.Mailing.update(mailingId, { acceptance_pdf_template: path });
      
      announce("Acceptance PDF uploaded successfully!");
    } catch (err) {
      console.error(err);
      setError("Failed to upload Acceptance PDF");
      setTimeout(() => setError(""), 3000);
    } finally {
      setUploadingAcceptance(false);
    }
  };

  const handleDeleteAcceptancePDF = async () => {
    setAcceptancePdfTemplate(null);
    
    // If editing existing mailing, save immediately
    if (existingMailingId) {
      try {
        await api.entities.Mailing.update(existingMailingId, { 
          acceptance_pdf_template: null 
        });
        announce("Acceptance PDF removed!");
      } catch (err) {
        console.error(err);
        setError("Failed to remove Acceptance PDF");
        setTimeout(() => setError(""), 3000);
      }
    }
  };

  // Modified handler for Postage Statement PDF - shows address modal after upload
  const handleUploadPostageStatementPDF = async (file) => {
    if (!file) return;

    const mailingId = await ensureSaved();
    if (!mailingId) return;


    setUploadingPostageStatement(true);
    setError("");

    try {
      const { path } = await api.upload('mailing-template', file, { mailingId });
      setPostageStatementTemplate(path);
      
      // If editing existing mailing, save immediately and clear generated overlay
      if (existingMailingId) {
        await api.entities.Mailing.update(existingMailingId, { 
          postage_statement_template: path
        });
      }
      
      announce("Postage Statement PDF uploaded successfully!");

      // The statement is overlaid with the address it is mailed for, so this is
      // the moment to ask. The Base44 app opened this here too; the port lost
      // the line, and the only way to reach the address was a button that
      // appears after the upload, which nobody would think to look for.
      if (!postageStatementAddress) setShowAddressModal(true);

      // Show address modal after successful upload
      setShowAddressModal(true);
    } catch (err) {
      console.error(err);
      setError("Failed to upload Postage Statement PDF");
      setTimeout(() => setError(""), 3000);
    } finally {
      setUploadingPostageStatement(false);
    }
  };

  const handleDeletePostageStatementPDF = async () => {
    setPostageStatementTemplate(null);
    setPostageStatementAddress(null); // Also clear address when template is deleted
    
    // If editing existing mailing, save immediately
    if (existingMailingId) {
      try {
        await api.entities.Mailing.update(existingMailingId, { 
          postage_statement_template: null,
          postage_statement_address: null
        });
        announce("Postage Statement PDF removed!");
      } catch (err) {
        console.error(err);
        setError("Failed to remove Postage Statement PDF");
        setTimeout(() => setError(""), 3000);
      }
    }
  };

  const handleSavePostageStatementAddress = async (addressData) => {
    setPostageStatementAddress(addressData);
    setShowAddressModal(false);

    // A mailing references an address row rather than holding a copy, so this
    // goes through an endpoint that finds or creates one. It used to PATCH the
    // object as `postage_statement_address`, which is not a column - the write
    // allowlist dropped it and the address was never stored.
    if (existingMailingId) {
      try {
        await api.mailings.setPostageAddress(existingMailingId, addressData);
        announce("Postage Statement address saved!");
      } catch (err) {
        console.error(err);
        setError("Failed to save address");
        setTimeout(() => setError(""), 3000);
      }
    }
  };

  const handleMailingDateChange = async (newDate) => {
    setMailingDate(newDate);
    
    // If editing existing mailing, update it in the database
    if (existingMailingId) {
      try {
        await api.entities.Mailing.update(existingMailingId, { mailing_date: newDate });
        announce("Mailing date updated! Regenerate PDFs to reflect the new date.");
      } catch (err) {
        console.error(err);
        setError("Failed to update mailing date");
        setTimeout(() => setError(""), 3000);
      }
    }
  };

  const handleMailingNameChange = async (newName) => {
    setMailingName(newName);
    
    // If editing existing mailing, update it in the database
    if (existingMailingId) {
      try {
        await api.entities.Mailing.update(existingMailingId, { mailing_name: newName });
        announce("Mailing name updated!");
      } catch (err) {
        console.error(err);
        setError("Failed to update mailing name");
        setTimeout(() => setError(""), 3000);
      }
    }
  };

  const handleOrderNumberChange = async (newOrderNumber) => {
    setOrderNumber(newOrderNumber);
    
    // If editing existing mailing, update it in the database
    if (existingMailingId) {
      try {
        await api.entities.Mailing.update(existingMailingId, { order_number: newOrderNumber });
        announce("Order number updated!");
      } catch (err) {
        console.error(err);
        setError("Failed to update order number");
        setTimeout(() => setError(""), 3000);
      }
    }
  };

  const persistMailing = async ({ silent = false } = {}) => {
    if (!mailingName || !orderNumber) {
      setError("Please fill in all required fields");
      return null;
    }

    if (routes.length === 0) {
      setError("No routes found. Please upload a valid PS Form 3602.");
      return null;
    }

    // The reconciliation gate. The routes and the form's own declared total are
    // read independently, so a disagreement means one of them was transcribed
    // wrong — and these counts go onto a USPS postage statement. The operator
    // has to look at the paper before this saves.
    if (reconciliation && !reconciliation.balanced) {
      setError(
        `${reconciliation.message} Correct the route table (or the piece counts) before saving.`
      );
      return null;
    }

    if (routes.some(r => r.needs_manual_review)) {
      setError(
        "Every route needs a confirmed destination post office before saving. " +
        "Unconfirmed destinations are highlighted below."
      );
      return null;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Check for duplicate order numbers
      const existingMailings = await api.entities.Mailing.filter({ order_number: orderNumber });
      const duplicates = existingMailings.filter(m => m.id !== existingMailingId);
      
      if (duplicates.length > 0) {
        setError(`Order number "${orderNumber}" already exists. Please use a unique order number.`);
        setLoading(false);
        return;
      }
      const totalPieces = routes.reduce((sum, r) => sum + (parseInt(r.pieces) || 0), 0);
      const totalBundles = routes.reduce((sum, r) => sum + (parseInt(r.bundles) || 0), 0);

      const postOfficesMap = {};
      routes.forEach(route => {
        const poName = route.post_office || "Default Post Office";
        if (!postOfficesMap[poName]) {
          postOfficesMap[poName] = {
            name: poName,
            routes: [],
            route_types: new Set(),
            total_pieces: 0,
            zip_codes: new Set(),
            address: route.post_office_address,
          };
        }
        postOfficesMap[poName].routes.push(route.route);
        if (route.route_type) {
          postOfficesMap[poName].route_types.add(route.route_type);
        }
        postOfficesMap[poName].total_pieces += parseInt(route.pieces) || 0;
        postOfficesMap[poName].zip_codes.add(route.zip);
      });

      const postOffices = Object.values(postOfficesMap).map(po => ({
        name: po.name,
        routes: po.routes,
        route_types: Array.from(po.route_types),
        total_pieces: po.total_pieces,
        zip_codes: Array.from(po.zip_codes),
        address: po.address,
      }));

      if (!(parseFloat(pieceWeight) > 0)) {
        setError('Enter the weight of a single piece, in ounces, before saving.');
        setLoading(false);
        return;
      }

      // The mailing and its routes go together in one call. Routes live in
      // their own table and the totals are maintained by trigger, so neither
      // fits through the generic entity endpoint - which silently dropped
      // every field it did not recognise, including the piece weight, whose
      // column is NOT NULL. Nothing was ever saved.
      const saved = await api.mailings.saveFromExtraction({
        ...(existingMailingId ? { id: existingMailingId } : {}),
        order_number: orderNumber,
        mailing_name: mailingName,
        mailing_date: mailingDate,
        piece_weight_lbs: parseFloat(pieceWeight) / OUNCES_PER_POUND,
        permit_holder: permitInfo.permit_holder || '',
        permit_number: permitInfo.permit_number || '',
        permit_type: permitInfo.permit_type || 'Permit Imprint',
        permit_post_office: permitInfo.post_office || permitInfo.permit_post_office || '',
        delivery_type: deliveryType,
        selected_paper_stock_id: selectedPaperStockId || null,
        selected_container_type: selectedContainerType || null,
        acceptance_pdf_template: acceptancePdfTemplate || null,
        postage_statement_template: postageStatementTemplate || null,
        routes: routes.map((route) => ({
          route: route.route,
          zip: route.zip,
          pieces: parseInt(route.pieces, 10),
          bundles: parseInt(route.bundles, 10) || 0,
          last_bundle_pieces: parseInt(route.last_bundle_pieces, 10) || parseInt(route.pieces, 10),
          route_type: route.route_type || 'C',
          post_office_id: route.post_office_id || null,
          // Sent when the destination has no stored row yet, which is every
          // route on a new mailing: USPS names the office, and it only becomes
          // a row when something saves it.
          post_office: route.post_office_id ? null : route.post_office_address ?? null,
          needs_review: route.needs_manual_review !== false,
        })),
      });

      setExistingMailingId(saved.id);
      if (!silent) announce(existingMailingId ? 'Mailing updated.' : 'Mailing saved.');

      // Packing needs a mailing to pack, so it could not run before this.
      if (selectedContainerType && selectedPaperStockId) {
        await runPacking(saved.id, selectedContainerType, selectedPaperStockId);
      }

      // Deliberately staying put. The Acceptance and Postage Statement uploads
      // are stored against the mailing, so they cannot be attached until it is
      // saved - and leaving the page a second and a half later meant the only
      // moment they could be attached was the moment the screen disappeared.
      // Saving is a step in the middle of this page's work, not the end of it.
      return saved;
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to save mailing");
      return null;
    } finally {
      setLoading(false);
    }
  };

  /** The Save button: persist, and say so. */
  const handleSave = async () => {
    await persistMailing();
  };

  const handleClosePDFViewer = () => {
    if (previewPDF && previewPDF.url.startsWith('blob:')) {
      URL.revokeObjectURL(previewPDF.url);
    }
    setPreviewPDF(null);
  };

  if (!parsedData) {
    return (
      <div className="page-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="header">
        <div className="header-title">{existingMailingId ? "Edit Mailing" : "Review Mailing"}</div>
        <button 
          className="btn btn-outline" 
          onClick={() => navigate(createPageUrl('ProcessedMailings'))}
        >
          <ArrowLeft size={20} />
          Back
        </button>
      </header>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/*
        * Everything that needs a saved mailing says so in one place.
        * Packing and the document uploads both require one, and both used to
        * fail quietly - packing set a message that was never rendered, and the
        * uploads reported "Failed to upload" without saying why.
        */}
      {!existingMailingId && (
        <div className="alert alert-warning">
          <strong>This mailing has not been saved yet.</strong> The Acceptance
          and Postage Statement uploads are stored against the mailing, so they
          need it saved first. Everything else on this page works now.
        </div>
      )}

      {packingError && <div className="alert alert-warning">{packingError}</div>}

      {/*
        * How the form was read, and whether it adds up.
        *
        * The counts here end up on a USPS postage statement, so the operator
        * should be able to see at a glance whether the routes were read out of
        * the PDF exactly or transcribed from a scan, and whether they match the
        * total the form itself declares.
        */}
      {reconciliation && (
        <div className={reconciliation.balanced ? "alert alert-success" : "alert alert-warning"}>
          {reconciliation.balanced ? (
            <>
              <strong>
                {reconciliation.route_total.toLocaleString()} pieces across {routes.length} route
                {routes.length === 1 ? "" : "s"}
              </strong>
              {reconciliation.stated_total !== null
                ? ", matching the total printed on the form."
                : ". The form declares no total to check against — verify the routes against the paper."}
              {extractionMethod === "text_layer" && (
                <span className="muted"> Read directly from the PDF, not transcribed.</span>
              )}
              {extractionMethod === "model" && (
                <span className="muted">
                  {" "}
                  Transcribed from a scan — check the counts against the paper form.
                </span>
              )}
            </>
          ) : (
            <strong>{reconciliation.message}</strong>
          )}
        </div>
      )}

      {/* Summary Section */}
      <div className="card" style={{ 
        background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", 
        color: "white",
        marginBottom: "1.5rem"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <input
              type="text"
              value={mailingName}
              onChange={(e) => handleMailingNameChange(e.target.value)}
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "6px",
                padding: "0.5rem",
                color: "white",
                fontSize: "1.125rem",
                fontWeight: 600,
                marginBottom: "0.5rem",
                width: "100%",
                maxWidth: "400px"
              }}
              placeholder="Mailing Name"
            />
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <span style={{ opacity: 0.9, fontSize: "0.875rem" }}>Order:</span>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => handleOrderNumberChange(e.target.value)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: "6px",
                  padding: "0.25rem 0.5rem",
                  color: "white",
                  fontSize: "0.875rem"
                }}
                placeholder="Order Number"
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
              <Calendar size={16} />
              <input
                type="date"
                value={mailingDate}
                onChange={(e) => handleMailingDateChange(e.target.value)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: "6px",
                  padding: "0.25rem 0.5rem",
                  color: "white",
                  fontSize: "0.875rem",
                  cursor: "pointer"
                }}
              />
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "2rem", fontWeight: 700 }}>{routes.length}</div>
            <div style={{ fontSize: "0.875rem", opacity: 0.9 }}>Routes</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "2rem", fontWeight: 700 }}>{totalPieces.toLocaleString()}</div>
            <div style={{ fontSize: "0.875rem", opacity: 0.9 }}>Pieces</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "2rem", fontWeight: 700 }}>{totalBundles}</div>
            <div style={{ fontSize: "0.875rem", opacity: 0.9 }}>Bundles</div>
          </div>
        </div>
      </div>

      {/* Permit Information */}
      <div className="form-section">
        <div className="form-title">Permit Information</div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Permit Holder *</label>
            <input
              type="text"
              className="form-input"
              value={permitInfo.permit_holder}
              onChange={(e) => setPermitInfo({ ...permitInfo, permit_holder: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Permit Number *</label>
            <input
              type="text"
              className="form-input"
              value={permitInfo.permit_number}
              onChange={(e) => setPermitInfo({ ...permitInfo, permit_number: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Permit Type</label>
            <select
              className="form-select"
              value={permitInfo.permit_type}
              onChange={(e) => setPermitInfo({ ...permitInfo, permit_type: e.target.value })}
            >
              <option value="Permit Imprint">Permit Imprint</option>
              <option value="Metered Mail">Metered Mail</option>
              <option value="Precanceled Stamp">Precanceled Stamp</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Delivery Type</label>
            <select
              className="form-select"
              value={deliveryType}
              onChange={(e) => setDeliveryType(e.target.value)}
            >
              <option value="Business &amp; Residential">Business &amp; Residential</option>
              <option value="Residential Only">Residential Only</option>
            </select>
            <p className="muted" style={{ marginTop: "0.375rem" }}>
              Printed on every facing slip. Read from the 3602 when the form
              declares it.
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Piece Weight (oz) *</label>
            <input
              type="number"
              step="0.01"
              className="form-input"
              value={pieceWeight}
              onChange={(e) => setPieceWeight(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 3-Column Upload Section: Acceptance, Postage Statement, Additional Documents */}
      <UploadDocumentsSection
        acceptancePdfTemplate={acceptancePdfTemplate} uploadingAcceptance={uploadingAcceptance} isDraggingAcceptance={isDraggingAcceptance}
        handleAcceptanceDragOver={handleAcceptanceDragOver} handleAcceptanceDragLeave={handleAcceptanceDragLeave} handleAcceptanceDrop={handleAcceptanceDrop}
        handleUploadAcceptancePDF={handleUploadAcceptancePDF} handleDeleteAcceptancePDF={handleDeleteAcceptancePDF} setPreviewPDF={setPreviewPDF}
        postageStatementTemplate={postageStatementTemplate} uploadingPostageStatement={uploadingPostageStatement} isDraggingPostageStatement={isDraggingPostageStatement}
        handlePostageStatementDragOver={handlePostageStatementDragOver} handlePostageStatementDragLeave={handlePostageStatementDragLeave} handlePostageStatementDrop={handlePostageStatementDrop}
        handleUploadPostageStatementPDF={handleUploadPostageStatementPDF} handleDeletePostageStatementPDF={handleDeletePostageStatementPDF}
        postageStatementAddress={postageStatementAddress} setShowAddressModal={setShowAddressModal}
        additionalDocuments={additionalDocuments} uploadingDocs={uploadingDocs} isDragging={isDragging}
        handleDragOver={handleDragOver} handleDragLeave={handleDragLeave} handleDrop={handleDrop}
        handleUploadDocuments={handleUploadDocuments} handleDeleteDocument={handleDeleteDocument}
      />

      {/* Shipping Address Override */}
      <ShippingOverrideSection
        value={shippingOverride}
        onChange={setShippingOverride}
      />

      {/* Two Column Layout: Destinations on Left, Box Packing on Right */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", marginBottom: "1.5rem" }}>
        {/* Left Column - Destination Post Offices */}
        <div className="form-section" style={{ marginBottom: 0 }}>
          <div className="form-title">
            Destination Post Offices ({Object.keys(destinationPostOffices).length})
          </div>
          <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
            EDDM mailings are delivered to specific USPS facilities based on route type.
            {routes.some(r => r.needs_manual_review) && (
              <span style={{ color: "#dc2626", fontWeight: 600, display: "block", marginTop: "0.5rem" }}>
                ⚠️ Some destinations need verification. Click &quot;Edit&quot; to correct addresses.
              </span>
            )}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem" }}>
            {Object.values(destinationPostOffices).map((po, index) => (
              <div 
                key={index}
                style={{
                  background: "white",
                  border: "2px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "1.25rem",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontSize: "1.125rem", 
                      fontWeight: 700, 
                      color: "#1e40af",
                      marginBottom: "0.25rem"
                    }}>
                      {po.name}
                    </div>
                    {po.address && (
                      <div style={{ 
                        fontSize: "0.875rem", 
                        color: "#64748b",
                        marginTop: "0.5rem"
                      }}>
                        <div>{po.address.address_line1}</div>
                        <div>{po.address.city}, {po.address.state} {po.address.zip}</div>
                      </div>
                    )}
                    {!po.address && po.zip_codes.size > 0 && (
                      <div style={{ 
                        fontSize: "0.875rem", 
                        color: "#64748b"
                      }}>
                        ZIP: {Array.from(po.zip_codes).join(", ")}
                      </div>
                    )}
                    {po.route_types.size > 0 && (
                      <div style={{ 
                        fontSize: "0.75rem", 
                        color: "#3b82f6",
                        marginTop: "0.5rem",
                        fontWeight: 600
                      }}>
                        Route Types: {Array.from(po.route_types).join(", ")}
                      </div>
                    )}
                  </div>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => {
                      setEditingPostOffice({
                        originalName: po.name,
                        newName: po.name,
                        newAddress: po.address || {
                          name: po.name,
                          address_line1: "",
                          city: "",
                          state: "",
                          zip: ""
                        }
                      });
                      setPostOfficeSearchName(po.name);
                      setError("");
                      setSuccess("");
                    }}
                    style={{ fontSize: "0.75rem" }}
                  >
                    Edit
                  </button>
                </div>
                <div style={{
                  paddingTop: "0.75rem",
                  borderTop: "1px solid #bfdbfe"
                }}>
                  <div style={{ 
                    fontSize: "0.875rem", 
                    color: "#475569", 
                    marginBottom: "0.5rem",
                    fontWeight: 600
                  }}>
                    Routes: {po.routes.length}
                  </div>
                  <div style={{ 
                    display: "flex", 
                    flexWrap: "wrap", 
                    gap: "0.5rem"
                  }}>
                    {po.routes.map((route, idx) => (
                      <span 
                        key={idx}
                        style={{
                          background: "#3b82f6",
                          color: "white",
                          padding: "0.25rem 0.75rem",
                          borderRadius: "6px",
                          fontSize: "0.875rem",
                          fontWeight: 600
                        }}
                      >
                        {route}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column - Paper Stock and Box Size Selection */}
        <div>
          <div className="form-section" style={{ marginBottom: "1.5rem" }}>
            <div className="form-title">Step 1: Choose Paper Stock</div>
            <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
              Select the paper stock used for this mailing. This determines bundle capacity.
            </p>
            
            {paperStocks.length === 0 ? (
              <p style={{ color: "#64748b", textAlign: "center", padding: "1rem" }}>
                No paper stocks defined. Go to Settings to add paper stock specifications.
              </p>
            ) : (
              <div className="form-group">
                <label className="form-label" htmlFor="paper-stock">Select Paper Stock</label>
                <select
                  id="paper-stock"
                  className="form-select"
                  value={selectedPaperStockId}
                  onChange={(e) => handlePaperStockChange(e.target.value)}
                  style={{ fontSize: "1rem" }}
                >
                  <option value="">-- Select paper stock --</option>
                  {paperStocks.map((paper) => (
                    <option key={paper.id} value={paper.id}>
                      {paper.paper_name} ({paper.width}&quot; × {paper.length}&quot; × {paper.thickness}&quot;)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="form-section" style={{ marginBottom: "1.5rem" }}>
            <div className="form-title">Step 2: Choose Container Type</div>
            <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
              Select whether to use boxes or trays. The system will automatically find the optimal sizes.
            </p>
            
            {!selectedPaperStockId ? (
              <p style={{ color: "#f59e0b", textAlign: "center", padding: "1rem", background: "#fffbeb", borderRadius: "8px" }}>
                ⚠️ Please select a paper stock first
              </p>
            ) : boxSizes.length === 0 ? (
              <p style={{ color: "#64748b", textAlign: "center", padding: "1rem" }}>
                No container sizes defined. Go to Settings to add boxes or trays.
              </p>
            ) : (
              <div>
                <div style={{
                  display: "flex",
                  gap: "1rem",
                  padding: "0.5rem",
                  background: "#f8fafc",
                  borderRadius: "8px",
                  border: "2px solid #e2e8f0",
                  marginBottom: "1rem"
                }}>
                  <button
                    type="button"
                    onClick={() => handleContainerTypeChange("Box")}
                    disabled={!selectedPaperStockId}
                    style={{
                      flex: 1,
                      padding: "1rem",
                      borderRadius: "6px",
                      border: "none",
                      background: selectedContainerType === "Box" ? "#fbbf24" : "transparent",
                      color: selectedContainerType === "Box" ? "#92400e" : "#64748b",
                      fontWeight: 600,
                      cursor: selectedPaperStockId ? "pointer" : "not-allowed",
                      transition: "all 0.2s",
                      fontSize: "1rem",
                      opacity: selectedPaperStockId ? 1 : 0.5
                    }}
                  >
                    📦 Boxes
                  </button>
                  <button
                    type="button"
                    onClick={() => handleContainerTypeChange("Tray")}
                    disabled={!selectedPaperStockId}
                    style={{
                      flex: 1,
                      padding: "1rem",
                      borderRadius: "6px",
                      border: "none",
                      background: selectedContainerType === "Tray" ? "#3b82f6" : "transparent",
                      color: selectedContainerType === "Tray" ? "white" : "#64748b",
                      fontWeight: 600,
                      cursor: selectedPaperStockId ? "pointer" : "not-allowed",
                      transition: "all 0.2s",
                      fontSize: "1rem",
                      opacity: selectedPaperStockId ? 1 : 0.5
                    }}
                  >
                    📋 Trays
                  </button>
                </div>

                {selectedContainerType && (
                  <p style={{ fontSize: "0.75rem", color: "#64748b", textAlign: "center" }}>
                    System will automatically select optimal {selectedContainerType.toLowerCase()} sizes to minimize total containers
                  </p>
                )}
              </div>
            )}

            {packingResult && (
              <div style={{
                marginTop: "1.5rem",
                padding: "1.5rem",
                background: "#f0fdf4",
                border: "2px solid #10b981",
                borderRadius: "12px"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                  <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#065f46" }}>
                    Packing Results
                  </div>
                  <div style={{
                    fontSize: "2rem",
                    fontWeight: 700,
                    color: "#10b981"
                  }}>
                    {packingResult.total_boxes} {packingResult.container_type}{packingResult.total_boxes !== 1 ? (packingResult.container_type === "Box" ? "es" : "s") : ''} Needed
                  </div>
                </div>

                <div style={{ fontSize: "0.875rem", color: "#064e3b", marginBottom: "1.5rem" }}>
                  Total: {packingResult.total_bundles} bundles
                  {packingResult.paper_stock_name && (
                    <>
                      <br />
                      <span style={{ fontSize: "0.75rem", color: "#16a34a" }}>
                        Based on: {packingResult.paper_stock_name} • Auto-optimized {packingResult.container_type?.toLowerCase() || 'container'} selection
                      </span>
                    </>
                  )}
                </div>

                {/* Containers */}
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  {packingResult.boxes.map((box, index) => (
                    <div
                      key={index}
                      style={{
                        background: "white",
                        padding: "1rem",
                        borderRadius: "8px",
                        border: "1px solid #bbf7d0"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                        <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#065f46" }}>
                          {packingResult.container_type || 'Container'} #{box.container_number}
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "#064e3b" }}>
                          {box.bundles} bundles{box.container_size ? ` • ${box.container_size.name}` : ''}
                        </div>
                      </div>

                      <div style={{ marginBottom: "0.5rem" }}>
                        <div style={{ fontSize: "0.875rem", color: "#3b82f6", fontWeight: 600, marginBottom: "0.25rem" }}>
                          → {box.post_office_name || 'Destination not set'}
                        </div>
                      </div>

                      <div style={{ fontSize: "0.875rem", color: "#064e3b", marginBottom: "0.5rem" }}>
                        <strong>Routes:</strong> {box.routes.map(r => `${r.route} (${r.bundles})`).join(", ")}
                      </div>

                      {box.container_size && (
                        <div style={{ fontSize: "0.75rem", color: "#16a34a", marginBottom: "0.5rem" }}>
                          Size: {box.container_size.length}&quot; × {box.container_size.width}&quot; × {box.container_size.height}&quot;
                        </div>
                      )}

                      <div style={{ 
                        fontSize: "0.875rem", 
                        color: "#065f46", 
                        fontWeight: 600,
                        padding: "0.5rem",
                        background: "#f0fdf4",
                        borderRadius: "6px",
                        marginTop: "0.5rem"
                      }}>
                        Weight: {box.weight ? box.weight.toFixed(2) : '0.00'} lbs
                        <span style={{ fontSize: "0.75rem", color: "#16a34a", marginLeft: "0.5rem" }}>
                          ({box.pieces} pieces + {packingResult.container_type?.toLowerCase() || 'container'})
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Routes Extracted Section */}
      <div className="form-section">
        <div className="form-title">
          Routes Extracted
        </div>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", 
          gap: "1rem",
          marginTop: "1rem"
        }}>
          {routes.map((route, index) => (
            <div 
              key={index} 
              style={{ 
                background: "white",
                border: route.needs_manual_review ? "2px solid #fbbf24" : "2px solid #e5e7eb",
                borderRadius: "12px",
                padding: "1rem",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                position: "relative"
              }}
            >
              {route.needs_manual_review && (
                <div style={{
                  position: "absolute",
                  top: "0.5rem",
                  right: "0.5rem",
                  background: "#fbbf24",
                  color: "white",
                  fontSize: "0.75rem",
                  padding: "0.25rem 0.5rem",
                  borderRadius: "4px",
                  fontWeight: 600
                }}>
                  VERIFY
                </div>
              )}
              
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center",
                marginBottom: "0.75rem",
                paddingBottom: "0.5rem",
                borderBottom: "1px solid #e5e7eb"
              }}>
                <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#1e40af" }}>
                  {route.route}
                </div>
                <div style={{ 
                  fontSize: "0.75rem", 
                  color: "#6b7280",
                  fontWeight: 500
                }}>
                  {route.zip}
                </div>
              </div>

              <div style={{ fontSize: "0.75rem", marginBottom: "0.5rem", color: "#374151" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                  <span style={{ color: "#6b7280" }}>Mailpieces:</span>
                  <strong>{route.pieces.toLocaleString()}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                  <span style={{ color: "#6b7280" }}>Bundles (x100 each):</span>
                  <strong>{route.bundles}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <span style={{ color: "#6b7280" }}>Last bundle:</span>
                  <strong>{route.lastBundlePieces} pieces</strong>
                </div>
              </div>

              <div style={{ 
                fontSize: "0.7rem", 
                paddingTop: "0.5rem",
                borderTop: "1px solid #e5e7eb",
                marginBottom: "0.5rem"
              }}>
                <span style={{ fontWeight: 500, color: "#6b7280" }}>Destination:</span>
                <div style={{ marginTop: "0.25rem", color: "#374151", fontWeight: 600 }}>
                  {route.post_office}
                </div>
                {route.post_office_address && (
                  <div style={{ marginTop: "0.25rem", color: "#64748b" }}>
                    {route.post_office_address.city}, {route.post_office_address.state}
                  </div>
                )}
              </div>

              <button
                className="btn btn-sm btn-outline"
                onClick={() => {
                  setEditingRoute(index);
                  setEditingRouteSearch(""); // Clear search when opening edit modal
                  setError("");
                  setSuccess("");
                }}
                style={{ width: "100%", fontSize: "0.75rem", padding: "0.375rem" }}
              >
                Edit Destination
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "2rem" }}>
        <button
          className="btn btn-outline"
          onClick={() => navigate(createPageUrl('ProcessedMailings'))}
        >
          Cancel
        </button>
        <button
          className="btn btn-success"
          onClick={handleSave}
          disabled={loading}
        >
          <Save size={20} />
          {loading ? (existingMailingId ? "Updating..." : "Saving...") : (existingMailingId ? "Update Mailing" : "Save Mailing")}
        </button>
      </div>

      {/* Edit Post Office Modal */}
      {editingPostOffice && (
        <EditPostOfficeModal
          editingPostOffice={editingPostOffice}
          setEditingPostOffice={setEditingPostOffice}
          postOfficeSearchName={postOfficeSearchName}
          setPostOfficeSearchName={setPostOfficeSearchName}
          handleSearchPostOffice={handleSearchPostOffice}
          handleUpdatePostOffice={handleUpdatePostOffice}
          searchingPostOffice={searchingPostOffice}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}

      {/* Edit Destination Modal */}
      {editingRoute !== null && (
        <EditRouteModal
          editingRoute={editingRoute}
          routes={routes}
          setEditingRoute={setEditingRoute}
          editingRouteSearch={editingRouteSearch}
          setEditingRouteSearch={setEditingRouteSearch}
          handleSearchRouteDestination={handleSearchRouteDestination}
          handleUpdateRouteDestination={handleUpdateRouteDestination}
          searchingRouteDestination={searchingRouteDestination}
          setError={setError}
          setSuccess={setSuccess}
          routeSearchLLMResult={routeSearchLLMResult}
          setRouteSearchLLMResult={setRouteSearchLLMResult}
        />
      )}

      {/* Postage Statement Address Modal */}
      {showAddressModal && (
        <PostageStatementAddressModal
          onClose={() => setShowAddressModal(false)}
          onSave={handleSavePostageStatementAddress}
          existingAddress={postageStatementAddress}
        />
      )}

      {/* PDF Viewer Modal */}
      {previewPDF && (
        <PDFViewerModal
          pdfUrl={previewPDF.url}
          title={previewPDF.title}
          onClose={handleClosePDFViewer}
        />
      )}
    </div>
  );
}